import React from 'react'
import { Tag } from 'primereact/tag'

import { formatCurrency } from '../../shared/utils'
import { ALWAYS_EXCLUDED_STATUSES } from './reportFilters'

// States, above a manufacturer report, exactly what the current filters counted
// and what they left out — including the cancelled orders that are never
// billable. These reports are attached to billing invoices, so a reader needs to
// be able to see why a total is what it is without re-deriving the filters.
//
// `criteria` and `excludedByStatus` come straight from the report endpoint;
// `excludedValueLabel` names what the excluded amounts represent (line-item
// value on the aggregate reports, order totals on the reconciliation report).
export default function ReportScopeSummary({
  criteria,
  excludedByStatus = [],
  excludedValueLabel = 'order value',
  className = '',
}) {
  const included = criteria?.statusesIncluded || []
  const alwaysExcluded = ALWAYS_EXCLUDED_STATUSES.map((entry) => entry.status)

  // Surface the statuses that actually cost the report something, the
  // never-billable ones (worth stating even at zero), and any whose cost the
  // endpoint did not count — silently dropping those would be the same gap this
  // panel exists to close.
  const excluded = excludedByStatus.filter(
    (entry) =>
      typeof entry.orders !== 'number' ||
      entry.orders > 0 ||
      alwaysExcluded.includes(entry.status)
  )

  const reasonFor = (status) =>
    ALWAYS_EXCLUDED_STATUSES.find((entry) => entry.status === status)?.reason ||
    "Outside this report's selected statuses."

  return (
    <div
      className={`border rounded p-3 mb-2 bg-light ${className}`}
      data-testid='report-scope-summary'
    >
      <div className='d-flex flex-wrap align-items-center gap-2 mb-2'>
        <span className='fw-semibold text-uppercase small text-muted'>
          Included
        </span>
        {included.length > 0 ? (
          included.map((status) => (
            <Tag key={status} value={status} severity='success' rounded />
          ))
        ) : (
          <span className='text-muted small'>Nothing — adjust the filters.</span>
        )}

        <span className='text-muted'>·</span>
        <span className='small text-muted'>Origins Direct orders only</span>
        {(criteria?.startDate || criteria?.endDate) && (
          <>
            <span className='text-muted'>·</span>
            <span className='small text-muted'>
              {criteria.startDate || 'start'} to {criteria.endDate || 'today'}
            </span>
          </>
        )}
      </div>

      <div className='d-flex flex-wrap align-items-start gap-2'>
        <span className='fw-semibold text-uppercase small text-muted pt-1'>
          Excluded
        </span>
        {excluded.length > 0 ? (
          <div className='d-flex flex-column gap-1'>
            {excluded.map((entry) => (
              <div
                key={entry.status}
                className='d-flex flex-wrap align-items-center gap-2'
              >
                <Tag
                  value={entry.status}
                  severity={
                    alwaysExcluded.includes(entry.status) ? 'danger' : 'warning'
                  }
                  rounded
                />
                <span className='small text-muted'>
                  {typeof entry.orders === 'number' && (
                    <>
                      {entry.orders} order{entry.orders === 1 ? '' : 's'}
                      {typeof entry.total === 'number' && (
                        <>
                          {' '}
                          · {formatCurrency(entry.total)} {excludedValueLabel}
                        </>
                      )}
                      {' — '}
                    </>
                  )}
                  {reasonFor(entry.status)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <span className='small text-muted pt-1'>Nothing.</span>
        )}
      </div>
    </div>
  )
}
